import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, useAuth } from '../store/auth.store'
import { useImpersonation } from '../store/impersonation.store'
import type { Case, PaginatedResponse } from '../types'

export function useCases(params?: { page?: number; limit?: number; search?: string; statusId?: string; priorityId?: string; typeId?: string; assignedTo?: string; areaId?: string; slaStatus?: string; hasAlerts?: boolean }) {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.limit) searchParams.set('limit', String(params.limit))
  if (params?.search) searchParams.set('search', params.search)
  if (params?.statusId) searchParams.set('statusId', params.statusId)
  if (params?.priorityId) searchParams.set('priorityId', params.priorityId)
  if (params?.typeId) searchParams.set('typeId', params.typeId)
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo)
  if (params?.areaId) searchParams.set('areaId', params.areaId)
  if (params?.slaStatus) searchParams.set('slaStatus', params.slaStatus)
  if (params?.hasAlerts) searchParams.set('hasAlerts', String(params.hasAlerts))
  if (impersonateMailboxId) searchParams.set('mailboxId', impersonateMailboxId)
  const qs = searchParams.toString()
  return useQuery<PaginatedResponse<Case>>({
    queryKey: ['cases', user?.id, params, impersonateMailboxId],
    queryFn: () => api.get<PaginatedResponse<Case>>(`/cases${qs ? `?${qs}` : ''}`),
    refetchInterval: 15_000,
  })
}

export function useCase(id: string) {
  const api = useApi()
  return useQuery<Case>({
    queryKey: ['case', id],
    queryFn: () => api.get<Case>(`/cases/${id}`),
    enabled: !!id,
  })
}

export function useCaseStats() {
  const api = useApi()
  const { impersonateMailboxId } = useImpersonation()
  const { user } = useAuth()
  const qs = impersonateMailboxId ? `?mailboxId=${impersonateMailboxId}` : ''
  return useQuery({
    queryKey: ['case-stats', user?.id, impersonateMailboxId],
    queryFn: () => api.get(`/cases/stats${qs}`),
    refetchInterval: 15_000,
  })
}

export function useUpdateCaseStatus() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, statusName, reason }: { id: string; statusName: string; reason?: string }) =>
      api.patch(`/cases/${id}/status/${statusName}`, { reason }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['case', id] })
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['case-stats'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useCaseReferences() {
  const api = useApi()
  return useQuery({
    queryKey: ['case-references'],
    queryFn: () => api.get<any>('/cases/references'),
    staleTime: 1000 * 60 * 30,
  })
}

export function useAssignCase() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, assignedTo, coAssignees }: { id: string; assignedTo: string; coAssignees?: string[] }) =>
      api.patch(`/cases/${id}/assign`, { assignedTo, coAssignees }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['case', id] })
      qc.invalidateQueries({ queryKey: ['cases'] })
    },
  })
}

export function useCreateCase() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      subject: string
      description: string
      mailboxId: string
      typeId?: string
      priorityId?: string
      riskLevelId?: string
      source?: string
    }) => api.post('/cases', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['case-stats'] })
    },
  })
}

export function useAddCommunication() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string
      data: {
        type: string
        subject: string
        body: string
        htmlBody?: string
        recipients?: string[]
        cc?: string[]
        emailId?: string
        attachments?: Array<{ id: string; url: string; filename: string; mimeType: string; size: number; localPath: string }>
      }
    }) => api.post(`/cases/${caseId}/communications`, data),
    onSuccess: (_data, { caseId }) => {
      qc.invalidateQueries({ queryKey: ['case', caseId] })
      qc.invalidateQueries({ queryKey: ['cases'] })
    },
  })
}

export function useGenerateQuickResponse() {
  const api = useApi()
  return useMutation({
    mutationFn: ({ emailId, type, tone }: { emailId: string; type: string; tone: 'formal' | 'friendly' | 'empathetic' }) =>
      api.post('/ai/quick-response', { emailId, type, tone }),
  })
}

export function useEscalateCase() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, level, category, reason, notifyRoles, areaId }: { caseId: string; level: number; category?: string; reason: string; notifyRoles?: string[]; areaId?: string }) =>
      api.post(`/cases/${caseId}/escalate`, { level, category, reason, notifyRoles, areaId }),
    onSuccess: (_data, { caseId }) => {
      qc.invalidateQueries({ queryKey: ['case', caseId] })
      qc.invalidateQueries({ queryKey: ['cases'] })
      qc.invalidateQueries({ queryKey: ['escalations', caseId] })
    },
  })
}

export function useCaseEscalations(caseId: string) {
  const api = useApi()
  return useQuery({
    queryKey: ['escalations', caseId],
    queryFn: () => api.get(`/cases/${caseId}/escalations`),
    enabled: !!caseId,
  })
}

export function useGenerateCaseResponse() {
  const api = useApi()
  return useMutation({
    mutationFn: (payload: {
      caseId: string
      commType?: 'reply' | 'reply_all' | 'forward' | 'internal'
      tone?: 'formal' | 'friendly' | 'empathetic'
      customInstructions?: string
      recipient?: string
    }) => api.post<{ suggestedResponse: string; confidence: number; warnings: string[] }>('/ai/case-response', payload),
  })
}

export function useUpdateCaseTasks() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ caseId, tasks }: { caseId: string; tasks: any[] }) =>
      api.patch(`/cases/${caseId}/tasks`, { tasks }),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: ['case', caseId] })
      queryClient.invalidateQueries({ queryKey: ['cases'] })
    },
  })
}

export function useAnalyzeCaseHistory() {
  const api = useApi()
  return useMutation({
    mutationFn: (payload: { caseId: string }) =>
      api.post<{
        executiveSummary: string
        whatWasDone: string[]
        whatIsPending: string[]
        risksAndAlerts: string[]
        recommendedActions: string[]
        slaStatusAssessment: {
          isAtRisk: boolean
          explanation: string
        }
        confidence: number
      }>('/ai/case-analysis', payload),
  })
}

